import { useEffect, useState } from "react";
import { dashboardApi } from "../api";
import Avatar from "./Avatar";
import { TableSkeleton, useSlowEnoughToShow } from "./Skeleton";
import type { TimelineEntry } from "../types";
import { formatDate } from "../utils";
import { useT } from "../i18n";

/**
 * The vocabulary of the activity log is written for an audit trail
 * (`request_in_progress`). Nobody outside the room reads it that way, so each
 * action gets the sentence a person would actually say.
 */
/**
 * Every action a client can see, as a TRANSLATION KEY. It cannot hold the
 * sentence itself: `t()` needs a language, and a module-level constant is built
 * once at import — before anybody has chosen one. So the map holds keys and the
 * sentence is resolved at render, per reader.
 */
const WORDING: Record<string, string> = {
  // Written by a month close that rebilled this request. A client finding a job
  // in a different month with no explanation is the unexplained movement the
  // close exists to end.
  billing_month_moved: "timeline.billing_month_moved",
  // Sharing is on the request's own timeline, not just an admin log: a client
  // should be able to see that a link to their work exists, and when it was
  // pulled back.
  work_shared: "timeline.work_shared",
  share_revoked: "timeline.share_revoked",
  request_created: "timeline.request_created",
  request_approved: "timeline.request_approved",
  request_in_progress: "timeline.request_in_progress",
  request_completed: "timeline.request_completed",
  request_rejected: "timeline.request_rejected",
  request_canceled: "timeline.request_canceled",
  request_pending: "timeline.request_pending",
  // These are logged against a request too, and fell through to the raw action
  // name — the hand-off screen was showing "work delivered" in lower case next to
  // properly written sentences.
  work_delivered: "timeline.work_delivered",
  brief_attached: "timeline.brief_attached",
  attachment_removed: "timeline.attachment_removed",
  offline_delivery_logged: "timeline.offline_delivery_logged",
  feedback_given: "timeline.feedback_given",
  stage_changed: "timeline.stage_changed",
  promised_date_set: "timeline.promised_date_set",
};

/**
 * The map has now been the thing I forgot three times — once per feature that added
 * an action. So the FALLBACK has to be presentable rather than merely present.
 *
 * In Arabic there is no presentable fallback: `stage_changed` de-underscored is
 * still English, and title-casing it does not make it Arabic. So an unmapped
 * action falls back to a written word rather than to the raw action name — a
 * reader learns nothing from `offline_delivery_logged` in either language, and
 * an English token inside an Arabic column is worse than a vague one.
 */
export function wording(action: string, t: (key: string) => string): string {
  const key = WORDING[action];
  if (key) return t(key);
  return t("timeline.updated");
}


export default function RequestTimeline({ requestId }: { requestId: string }) {
  const t = useT();
  const [entries, setEntries] = useState<TimelineEntry[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setEntries(null);
    setFailed(false);
    dashboardApi.timeline(requestId)
      .then((data) => { if (live) setEntries(data.timeline); })
      // A timeline that cannot load must not read as a request with no history.
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [requestId]);

  const showSkeleton = useSlowEnoughToShow(!entries && !failed);

  if (failed) return <p className="timeline-empty">{t("requests.timelineFailed")}</p>;
  if (!entries) return showSkeleton ? <TableSkeleton rows={3} label="Loading the request history" /> : null;
  if (!entries.length) return <p className="timeline-empty">{t("requests.timelineEmpty")}</p>;

  return (
    <ol className="timeline">
      {entries.map((entry, index) => (
        <li key={`${entry.at}-${index}`} className={index === entries.length - 1 ? "timeline-item is-latest" : "timeline-item"}>
          <Avatar name={entry.actor} size={22} />
          <div className="timeline-body">
            <strong>{wording(entry.action, t)}</strong>
            <span className="timeline-meta">
              {formatDate(entry.at, true)}
              {entry.actor ? ` · ${entry.actor}` : ""}
            </span>
          </div>
        </li>
      ))}
    </ol>
  );
}
